import { Plus } from "lucide-react";
import { fmtDateMed } from "../../lib/dates";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { Button } from "../../components/ui/button";

/**
 * Add a single shift entry by picking shift + employee + date independently.
 * Triggered from the roster header "+ Add Entry" button.
 */
export const BulkAddDialog = ({ open, form, onFormChange, shifts, employees, onConfirm, onClose }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-xl">
      <DialogHeader>
        <DialogTitle className="text-lg tracking-tight font-bold">Add Shift to Date</DialogTitle>
      </DialogHeader>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
        <div>
          <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Shift Type *
          </Label>
          <Select
            value={form.shift_id}
            onValueChange={(val) => onFormChange({ ...form, shift_id: val })}
          >
            <SelectTrigger className="mt-1" data-testid="bulk-shift-select">
              <SelectValue placeholder="Select shift type" />
            </SelectTrigger>
            <SelectContent>
              {shifts.map((shift) => (
                <SelectItem key={shift.shift_id} value={shift.shift_id}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: shift.color }} />
                    {shift.name} ({shift.start_time}—{shift.end_time})
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Employee *
          </Label>
          <Select
            value={form.employee_id}
            onValueChange={(val) => onFormChange({ ...form, employee_id: val })}
          >
            <SelectTrigger className="mt-1" data-testid="bulk-employee-select">
              <SelectValue placeholder="Select employee" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((emp) => (
                <SelectItem key={emp.employee_id} value={emp.employee_id}>
                  {emp.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Date *
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full mt-1 justify-start text-left font-normal"
                data-testid="bulk-date-btn"
              >
                {form.date ? fmtDateMed(form.date) : "Pick date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={form.date}
                onSelect={(date) => onFormChange({ ...form, date: date })}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex justify-end gap-3 pt-4 sm:col-span-2">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="cancel-bulk-add"
          >
            Cancel
          </Button>
          <Button
            className="bg-[#002FA7] hover:bg-[#00227A] text-white"
            disabled={!form.shift_id || !form.employee_id || !form.date}
            onClick={onConfirm}
            data-testid="confirm-bulk-add"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
